import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactElement } from 'react';
import { MemoryRouter } from 'react-router-dom';

import type { DemoNavTarget } from './cues';
import { SessionController, type SessionControllerDeps, type SessionStore } from '../SessionController';
import { Button } from '../../ui/components/core/Button';
import { clearMatchSession } from '../../session/match-session';
import { usePresentationPalette } from '../presentation/usePresentationPalette';
import { FESTIVAL_ASSETS } from './festival-assets';
import { Z } from './z-layers';
import { createPresentationAudio, type PresentationAudio, type PresentationAudioFactory } from './presentation-audio';

type Team = 'red' | 'blue';
type ProjectorView = 'field' | Team;

interface SeatStatus { name: string; claimed: boolean; connected: boolean }
interface PresentationStatus {
  phase: string;
  generation: number;
  matchId: string | null;
  red?: SeatStatus;
  blue?: SeatStatus;
  result?: unknown;
  palette?: { red: string; blue: string };
  clockRemainingTicks?: number | null;
}
interface PresentationSession { token: string; matchId: string }
interface PreviewResponse {
  team: Team;
  readOnly: true;
  previewSession: PresentationSession;
}
interface CreateResponse extends PresentationStatus {
  presenterToken: string;
  spectatorSession: PresentationSession;
}
const PRESENTER_STORAGE_KEY = 'waggle-presentation-projector';
export function bumblebeeVolume(clockRemainingTicks: number): number {
  if (clockRemainingTicks <= 0 || clockRemainingTicks > 900) return 0;
  const progress = Math.min(450, 900 - clockRemainingTicks) / 450;
  return 0.35 * progress * progress;
}

function restoredPresenter(): Pick<CreateResponse, 'presenterToken' | 'spectatorSession'> | null {
  try {
    const value = JSON.parse(window.sessionStorage.getItem(PRESENTER_STORAGE_KEY) ?? 'null') as Partial<CreateResponse> | null;
    return value !== null && typeof value.presenterToken === 'string' &&
      typeof value.spectatorSession?.token === 'string' && typeof value.spectatorSession.matchId === 'string'
      ? { presenterToken: value.presenterToken, spectatorSession: value.spectatorSession }
      : null;
  } catch { return null; }
}

function rememberPresenter(value: Pick<CreateResponse, 'presenterToken' | 'spectatorSession'>): void {
  try { window.sessionStorage.setItem(PRESENTER_STORAGE_KEY, JSON.stringify(value)); } catch { /* in-memory state still runs the show */ }
}

export interface DemoLiveMatchSeams {
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  origin?: string;
  transportFactory: SessionControllerDeps['transportFactory'];
  scheduler?: SessionControllerDeps['scheduler'];
  rng?: SessionControllerDeps['rng'];
  createRenderer?: SessionControllerDeps['createRenderer'];
  createAudio?: PresentationAudioFactory;
}

function makeSpectatorStore(session: PresentationSession): SessionStore {
  let record = {
    token: session.token,
    matchId: session.matchId,
    role: 'spectator' as const,
    phase: 'connecting' as const,
    isHost: false,
  };
  return { get: () => record, set: (next) => { record = next as typeof record; }, clear: () => undefined };
}

function makePreviewStore(session: PresentationSession): SessionStore {
  let record = {
    token: session.token,
    matchId: session.matchId,
    // The preview deliberately asks the existing client shell to render Player View. The server
    // owns the actual authority and records this token as a spectator, so this display role can
    // never turn into mutation authority.
    role: 'player' as const,
    phase: 'connecting' as const,
    isHost: false,
  };
  return { get: () => record, set: (next) => { record = next as typeof record; }, clear: () => undefined };
}

export function DemoLiveMatchChapters({
  target,
  seams,
  onStarted,
  onReset,
}: {
  target: DemoNavTarget | null;
  seams: DemoLiveMatchSeams;
  /** Called once a `POST /presentation/start` is accepted, so the click that starts the match is
   * also the one that shows it (plan **D-3**). Never called on a refusal — a 409 must leave the
   * deck where it is, showing the "Both players must be connected" line. */
  onStarted?: () => void;
  /** The deliberate counterpart: called once a `POST /presentation/reset` is accepted, so the room
   * goes back to the Red/Blue join window in one action. D-2 removed the *accidental* version of
   * this — the served phase overriding the cue — and that removal was right, because the same
   * mechanism dragged the projector backwards on any dropped socket. A refused reset navigates
   * nothing, like a refused Start. */
  onReset?: () => void;
}): ReactElement | null {
  const fetchImpl = seams.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const root = (seams.baseUrl ?? '').replace(/\/+$/, '');
  const [status, setStatus] = useState<PresentationStatus | null>(null);
  const restored = useMemo(restoredPresenter, []);
  const [presenterToken, setPresenterToken] = useState<string | null>(restored?.presenterToken ?? null);
  const [spectator, setSpectator] = useState<PresentationSession | null>(restored?.spectatorSession ?? null);
  const [redName, setRedName] = useState('');
  const [blueName, setBlueName] = useState('');
  const [view, setView] = useState<ProjectorView>('field');
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const soundtrackRef = useRef<{ matchId: string; audio: PresentationAudio } | null>(null);

  usePresentationPalette(status?.palette);

  useEffect(() => {
    const stop = (): void => {
      const current = soundtrackRef.current;
      if (current === null) return;
      current.audio.pause();
      current.audio.currentTime = 0;
      soundtrackRef.current = null;
    };
    const remaining = status?.clockRemainingTicks;
    if (
      target !== 'liveMatch' || status?.phase !== 'running' || status.matchId === null ||
      remaining === null || remaining === undefined || remaining <= 0 || remaining > 900
    ) {
      if (status?.phase !== 'running' || target !== 'liveMatch') stop();
      return undefined;
    }
    if (soundtrackRef.current?.matchId !== status.matchId) {
      stop();
      const audio = (seams.createAudio ?? createPresentationAudio)(FESTIVAL_ASSETS.flightOfBumblebee);
      audio.volume = 0;
      soundtrackRef.current = { matchId: status.matchId, audio };
      void Promise.resolve(audio.play()).catch(() => undefined);
    }
    soundtrackRef.current.audio.volume = bumblebeeVolume(remaining);
    return undefined;
  }, [seams.createAudio, status?.clockRemainingTicks, status?.matchId, status?.phase, target]);

  useEffect(() => () => {
    soundtrackRef.current?.audio.pause();
    if (soundtrackRef.current !== null) soundtrackRef.current.audio.currentTime = 0;
    soundtrackRef.current = null;
  }, []);

  useEffect(() => {
    let live = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const poll = async (): Promise<void> => {
      try {
        const response = await fetchImpl(`${root}/presentation/status`);
        if (response.ok && live) setStatus(await response.json() as PresentationStatus);
      } finally {
        if (live) timer = setTimeout(() => void poll(), 1_000);
      }
    };
    void poll();
    return () => { live = false; if (timer !== undefined) clearTimeout(timer); };
  }, [fetchImpl, root]);

  useEffect(() => {
    if (status?.matchId !== null) return;
    // The coordinator is process-local by design. A browser may survive a server restart, so a
    // restored presenter capability is only a hint until status confirms that match still exists.
    try { window.sessionStorage.removeItem(PRESENTER_STORAGE_KEY); } catch { /* state still recovers */ }
    setPresenterToken(null);
    setSpectator(null);
    setPreview(null);
  }, [status?.matchId]);

  useEffect(() => {
    if (view === 'field' || presenterToken === null) return;
    let live = true;
    void fetchImpl(`${root}/presentation/preview/${view}`, {
      headers: { Authorization: `Bearer ${presenterToken}` },
    }).then(async (response) => {
      if (live && response.ok) setPreview(await response.json() as PreviewResponse);
    });
    return () => { live = false; };
  }, [fetchImpl, presenterToken, root, view]);

  const spectatorStore = useMemo(
    () => spectator === null ? null : makeSpectatorStore(spectator),
    [spectator],
  );

  const create = async (): Promise<void> => {
    setBusy(true);
    try {
      const response = await fetchImpl(`${root}/presentation/match`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ redName, blueName }),
      });
      if (!response.ok) { setNotice('The server could not create a fresh match.'); return; }
      const created = await response.json() as CreateResponse;
      setPresenterToken(created.presenterToken);
      setSpectator(created.spectatorSession);
      rememberPresenter(created);
      setStatus(created);
    } finally { setBusy(false); }
  };

  const disposeActiveProjection = (): void => {
    if (view === 'field' && spectator !== null) {
      clearMatchSession({ sessionToken: spectator.token, matchId: spectator.matchId });
    } else if (view !== 'field' && preview?.team === view) {
      clearMatchSession({
        sessionToken: preview.previewSession.token,
        matchId: preview.previewSession.matchId,
      });
    }
  };

  const mutate = async (path: string): Promise<void> => {
    if (presenterToken === null) return;
    setBusy(true);
    try {
      const response = await fetchImpl(`${root}${path}`, {
        method: 'POST', headers: { Authorization: `Bearer ${presenterToken}` },
      });
      if (response.ok && response.status !== 204) {
        const next = await response.json() as PresentationStatus & Partial<CreateResponse>;
        setStatus(next);
        if (path === '/presentation/reset') {
          disposeActiveProjection();
          setView('field');
          setPreview(null);
          setNotice(null);
          onReset?.();
        }
        if (next.presenterToken !== undefined && next.spectatorSession !== undefined) {
          setPresenterToken(next.presenterToken);
          setSpectator(next.spectatorSession);
          rememberPresenter({ presenterToken: next.presenterToken, spectatorSession: next.spectatorSession });
        }
        if (path === '/presentation/start') onStarted?.();
      } else if (!response.ok) {
        setNotice(response.status === 409 ? 'Both players must be connected before Start.' : 'Presenter action was refused.');
      }
    } finally { setBusy(false); }
  };

  const selectView = (next: ProjectorView): void => {
    if (next === view) return;
    disposeActiveProjection();
    setPreview(null);
    setView(next);
  };

  useEffect(() => {
    if (target !== 'liveMatch') return undefined;
    // `MatchSession` deliberately survives ordinary screen navigation. A presentation projection
    // is different: leaving its cue or unmounting relinquishes that temporary stream completely,
    // so an invisible Field/Red/Blue controller cannot keep writing the singleton client store.
    return () => {
      if (view === 'field' && spectator !== null) {
        clearMatchSession({ sessionToken: spectator.token, matchId: spectator.matchId });
      } else if (view !== 'field' && preview?.team === view) {
        clearMatchSession({
          sessionToken: preview.previewSession.token,
          matchId: preview.previewSession.matchId,
        });
      }
    };
  }, [preview, spectator, target, view]);

  // The coordinator may poll throughout the deck, but its UI enters only on an explicit nav cue.
  // Otherwise an uncreated match would cover every earlier slide with the setup form.
  if (target === null) return null;
  if (status === null) return <div style={SURFACE}><h1>Set up Live Match</h1><p>Connecting to the presentation server…</p></div>;
  if (status.matchId === null) {
    return (
      <main style={SURFACE} data-testid="presentation-setup">
        <section style={CARD}>
          <p style={EYEBROW}>Waggle · two-player showcase</p>
          <h1>Set up Live Match</h1>
          <label style={LABEL}>Red player name<input style={INPUT} aria-label="Red player name" value={redName} onChange={(event) => setRedName(event.target.value)} /></label>
          <label style={LABEL}>Blue player name<input style={INPUT} aria-label="Blue player name" value={blueName} onChange={(event) => setBlueName(event.target.value)} /></label>
          <Button disabled={busy} onClick={() => void create()}>Create match</Button>
          {notice !== null && <p role="alert">{notice}</p>}
        </section>
      </main>
    );
  }

  if (status.phase === 'lost') {
    return <main style={SURFACE}><section style={CARD}><p style={EYEBROW}>Match unavailable</p><h1>The server lost this Live Match</h1><p>Live Matches are intentionally not resumed after a server failure.</p><Button disabled={busy || presenterToken === null} onClick={() => void mutate('/presentation/reset')}>Create fresh match</Button></section></main>;
  }
  /**
   * **The cue the presenter is on wins over the served phase** (deathmatch rehearsal, 2026-08-12
   * — plan **D-2**). This used to read `target === 'setup' || phase is waiting/ready`, with the
   * phase winning: any seat losing its socket flipped `ready -> waitingForSeats` and dragged the
   * projector back to the waiting room from wherever the presenter had navigated. A phase is a
   * fact about the seats, not an instruction to the screen the room is watching.
   *
   * `phase` keeps exactly one job — lighting the Start button (`ready` below), computed
   * server-side from both seats being connected. The server's own 409 `players_not_connected` is
   * the real guard; the button state is a courtesy. Phase is still consulted for a cue that names
   * no projector view at all (the tournament `nav` targets, which this component does not own).
   */
  const cueNamesView = target === 'setup' || target === 'liveMatch';
  if (
    target === 'setup' ||
    (!cueNamesView && (status.phase === 'waitingForSeats' || status.phase === 'ready'))
  ) {
    const ready = status.phase === 'ready';
    return (
      <main style={SURFACE} data-testid="presentation-waiting-room">
        <section style={{ ...CARD, maxWidth: 920 }}>
          <p style={EYEBROW}>PvP Live Match</p><h1>Waiting for both player computers</h1>
          <p>Open <b>/demo/red</b> on Red and <b>/demo/blue</b> on Blue.</p>
          <div style={SEATS}>
            <SeatCard team="Red" color={status.palette?.red} seat={status.red} onRelease={() => void mutate('/presentation/seats/red/release')} />
            <SeatCard team="Blue" color={status.palette?.blue} seat={status.blue} onRelease={() => void mutate('/presentation/seats/blue/release')} />
          </div>
          <Button disabled={!ready || busy} onClick={() => void mutate('/presentation/start')}>Start Live Match</Button>
          {!ready && <p role="status">Start unlocks after both player devices are ready.</p>}
          {notice !== null && <p role="alert">{notice}</p>}
        </section>
      </main>
    );
  }

  const finished = status.phase === 'finished';
  return (
    <main style={SURFACE} data-testid="presentation-live-match">
      <div style={TOOLBAR}>
        {(['field', 'red', 'blue'] as const).map((choice) => <Button variant="ghost" key={choice} aria-pressed={view === choice} style={choice === 'field' ? undefined : { borderBottom: `4px solid ${status.palette?.[choice] ?? 'var(--line-strong)'}` }} onClick={() => selectView(choice)}>{choice === 'field' ? 'Field' : `${choice === 'red' ? 'Red' : 'Blue'} view`}</Button>)}
        <Button variant="danger" onClick={() => { if (window.confirm('Reset this match and release both seats?')) void mutate('/presentation/reset'); }}>Reset match</Button>
      </div>
      {finished && view === 'field' ? <ResultHold result={status.result} redName={status.red?.name} blueName={status.blue?.name} /> : view === 'field' ? (
        spectatorStore === null ? <p>Connecting projector…</p> : (
          /**
           * **The prompts live in the HUD's own panels** (`pvp-ui-fixes` W3). A floating
           * `PvpPromptFeed` used to sit here, pinned 360px wide at the top-right — over the Blue
           * colony's side panel, and under the view toolbar. It predated the two-colony HUD that
           * superseded it: `ColonySidePanel` already draws prompts as cards per colony
           * (`match-hud` D-7), and this composition simply never passed the seams to fill them, so
           * `Match` fell back to player mode with a null token and both prompt sections were
           * absent. Passing them fills the panels and the aside is deleted rather than restyled.
           *
           * **Two bearers, because they are two capabilities.** The presentation token reads
           * `/presentation/prompts`, which is the only route carrying both colonies' authored
           * content. The projector's own spectator session reads `/live/stats`, which is open
           * board state (DESIGN §9) and takes a live session — the presentation token names no
           * session at all there. Sending either to the other's route is what the
           * `DemoLiveMatchChapters` capability-boundary test refuses.
           */
          <MemoryRouter initialEntries={['/match']}>
            <SessionController
              transportFactory={seams.transportFactory}
              lobby={{ baseUrl: root, fetchImpl }}
              sessionStore={spectatorStore}
              scheduler={seams.scheduler}
              rng={seams.rng}
              createRenderer={seams.createRenderer}
              matchHud={{
                mode: 'presenter',
                token: presenterToken,
                statsToken: spectator?.token ?? null,
                baseUrl: root,
                fetchImpl,
                // The seat names, known from the join window on — so the panels read "Ada" and
                // "Grace" from the first frame instead of "Colony 0"/"Colony 1" until each
                // player's first prompt happens to carry a name. Red is colony 0, blue colony 1:
                // the same mapping the palette override and the prompt feed already use. An
                // empty string reads as "unknown" downstream, so an unclaimed seat changes
                // nothing.
                names: { 0: status.red?.name ?? '', 1: status.blue?.name ?? '' },
              }}
            />
          </MemoryRouter>
        )
      ) : preview === null || preview.team !== view || presenterToken === null ? <p>Connecting Presenter Preview…</p> : (
        <section style={PREVIEW} data-testid={`presenter-${view}-preview`}>
          <div style={PREVIEW_LABEL}>Presenter Preview · {view.toUpperCase()} · read-only</div>
          <MemoryRouter key={`${view}:${preview.previewSession.token}`} initialEntries={[finished ? '/summary' : '/colony']}>
            <SessionController
              transportFactory={seams.transportFactory}
              lobby={{ baseUrl: root, fetchImpl }}
              sessionStore={makePreviewStore(preview.previewSession)}
              scheduler={seams.scheduler}
              rng={seams.rng}
              createRenderer={seams.createRenderer}
              pvp
              readOnlyPreview={{
                onAttempt: () => setNotice('Presenter Preview is read-only. Nothing was sent to the match.'),
                chat: { presenterToken, team: view },
              }}
            />
          </MemoryRouter>
          {notice !== null && <div role="status" style={PREVIEW_NOTICE}>{notice}</div>}
        </section>
      )}
    </main>
  );
}

function SeatCard({ team, color, seat, onRelease }: { team: string; color?: string; seat?: SeatStatus; onRelease: () => void }): ReactElement {
  return <article style={{ ...SEAT, borderColor: color ?? 'var(--hud-line)', boxShadow: `inset 0 5px ${color ?? 'var(--hud-line)'}` }}><h2>{team}</h2><strong>{seat?.name ?? `${team} Player`}</strong><p>{seat?.connected ? 'Device ready' : seat?.claimed ? 'Claimed · connecting' : 'Waiting to claim'}</p>{seat?.claimed && <Button variant="secondary" onClick={onRelease}>Release {team}</Button>}</article>;
}

function ResultHold({ result, redName = 'Red Player', blueName = 'Blue Player' }: { result: unknown; redName?: string; blueName?: string }): ReactElement {
  const placements = result !== null && typeof result === 'object' && Array.isArray((result as { placements?: unknown }).placements)
    ? (result as { placements: Array<{ colonyId?: unknown; colony_id?: unknown; rank?: unknown }> }).placements
    : [];
  const colonyOf = (entry: { colonyId?: unknown; colony_id?: unknown }): unknown => entry.colonyId ?? entry.colony_id;
  // **The lowest rank wins — never `rank === 1`** (Bugbot round 1, `pvp-ui-fixes`). The wire's one
  // production constructor assigns 0-based ranks ("`rank` 0 is the winner",
  // `waggle-server/src/match_thread.rs`), so the old equality check crowned the RUNNER-UP: the
  // room was shown the loser's name under "wins" while the seat cards swapped Winner/Runner-up.
  // Its test passed because the fixture was hand-written 1-based to match. Lowest-wins mirrors
  // the Match screen's own winner banner and the Summary (`sort by rank, take [0]`), so every
  // result surface reads the wire the same way.
  const winner = [...placements].sort(
    (a, b) => (typeof a.rank === 'number' ? a.rank : Infinity) - (typeof b.rank === 'number' ? b.rank : Infinity),
  )[0];
  const isWinner = (colony: number): boolean => winner !== undefined && colonyOf(winner) === colony;
  const winnerName = isWinner(0) ? redName : isWinner(1) ? blueName : null;
  return <section style={CARD} data-testid="presentation-result"><p style={EYEBROW}>Match complete</p><h1>{winnerName === null ? 'Live Match result' : `${winnerName} wins`}</h1><div style={SEATS}><div style={{ ...SEAT, borderColor: 'var(--colony-0)' }}><strong>Red · {redName}</strong><p>{isWinner(0) ? 'Winner' : 'Runner-up'}</p></div><div style={{ ...SEAT, borderColor: 'var(--colony-1)' }}><strong>Blue · {blueName}</strong><p>{isWinner(1) ? 'Winner' : 'Runner-up'}</p></div></div><p>The named result stays here until the presenter advances. Choose Red or Blue view for that player’s Summary.</p></section>;
}

const SURFACE: CSSProperties = { position: 'fixed', inset: 0, zIndex: Z.chapterSurface, display: 'grid', placeItems: 'center', padding: 32, boxSizing: 'border-box', overflow: 'auto', background: 'var(--surface-app)', color: 'var(--ink-1)', fontFamily: 'var(--font-body)' };
const CARD: CSSProperties = { width: 'min(680px, 100%)', padding: 36, borderRadius: 'var(--r-lg)', background: 'var(--surface-card)', boxShadow: 'var(--shadow-hud)', display: 'grid', gap: 18 };
const LABEL: CSSProperties = { display: 'grid', gap: 7, fontWeight: 700 };
const INPUT: CSSProperties = { minHeight: 'var(--hit-target)', boxSizing: 'border-box', borderRadius: 'var(--r-sm)', border: '1px solid var(--line-strong)', background: 'var(--surface-card)', color: 'var(--ink-1)', padding: '0 12px', font: 'inherit' };
const EYEBROW: CSSProperties = { margin: 0, textTransform: 'uppercase', letterSpacing: '.12em', color: 'var(--accent-strong)' };
const SEATS: CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 };
const SEAT: CSSProperties = { padding: 20, border: '2px solid var(--hud-line)', borderRadius: 'var(--r-md)' };
const TOOLBAR: CSSProperties = { position: 'fixed', top: 18, left: '50%', transform: 'translateX(-50%)', zIndex: Z.presenterToolbar, display: 'flex', gap: 8, padding: 8, borderRadius: 'var(--r-pill)', background: 'var(--surface-card)' };
const PREVIEW: CSSProperties = { position: 'fixed', inset: 0, overflow: 'hidden', background: 'var(--surface-app)' };
const PREVIEW_LABEL: CSSProperties = { position: 'fixed', top: 76, right: 18, zIndex: Z.previewLabel, padding: '8px 12px', borderRadius: 'var(--r-pill)', background: 'var(--surface-card)', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.08em' };
const PREVIEW_NOTICE: CSSProperties = { position: 'fixed', left: '50%', bottom: 112, zIndex: Z.previewNotice, transform: 'translateX(-50%)', padding: '12px 18px', borderRadius: 'var(--r-sm)', background: 'var(--ink-1)', color: 'var(--surface-card)', fontWeight: 700 };
